import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Calculator,
  Check,
  Clock3,
  Plane,
  Plus,
  Route,
  ShieldCheck,
  Video,
} from "lucide-react";
import { inr, sqm } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function PropertyCard({
  property: p,
  onLead,
  onCalc,
  onOpenDetail,
  inCompare,
  onToggleCompare,
}) {
  const total = p.plotSqYd * p.pricePerSqYd;
  const registered = p.reraStatus === "REGISTERED";

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col border border-border bg-card"
      data-testid={`property-card-${p.id}`}
    >
      <button
        onClick={() => onOpenDetail(p)}
        className="relative block h-44 w-full overflow-hidden border-b border-border text-left"
        data-testid={`parcel-link-${p.id}`}
      >
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover saturate-[.55] contrast-[1.05] transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="absolute left-3 top-3 flex items-center gap-1.5">
          {p.isNew && (
            <span
              className="bg-accent px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-accent-foreground"
              data-testid={`new-badge-${p.id}`}
            >
              New
            </span>
          )}
          <span className="border border-border bg-background/90 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] backdrop-blur">
            {p.zone} · {p.zoneLabel}
          </span>
        </div>
        <span
          className="absolute right-3 top-3 flex items-center gap-1.5 border border-border bg-background/90 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] backdrop-blur"
          data-testid={`rera-badge-${p.id}`}
        >
          {registered ? (
            <ShieldCheck className="h-3 w-3 text-accent" />
          ) : (
            <Clock3 className="h-3 w-3 text-ember" />
          )}
          RERA {registered ? "Registered" : "Applied"}
        </span>
        <p className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.2em] text-white/80">
          {p.coords}
        </p>
      </button>

      <div className="flex flex-1 flex-col p-5">
        <button
          onClick={() => onOpenDetail(p)}
          className="text-left"
          data-testid={`parcel-title-${p.id}`}
        >
          <h3 className="font-display text-xl font-medium tracking-tight transition-colors duration-200 hover:text-accent">
            {p.name}
          </h3>
        </button>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {p.surveyNo ? `Survey ${p.surveyNo}` : `Sector ${p.sector}`} · {p.type}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-px border border-border bg-border">
          <div className="bg-card p-2.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              TP Scheme
            </p>
            <p className="mt-0.5 font-mono text-sm font-bold">{p.zone}</p>
          </div>
          <div className="bg-card p-2.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              Plot Size
            </p>
            <p className="mt-0.5 font-mono text-sm font-bold">
              {p.plotSqYd.toLocaleString("en-IN")} SQ YD
            </p>
            <p className="font-mono text-[10px] text-muted-foreground">
              {sqm(p.plotSqYd).toLocaleString("en-IN")} SQ M
            </p>
          </div>
          <div className="bg-card p-2.5">
            <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              <Route className="h-3 w-3" /> Expressway
            </p>
            <p className="mt-0.5 font-mono text-sm font-bold">{p.expresswayKm} KM</p>
          </div>
          <div className="bg-card p-2.5">
            <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              <Plane className="h-3 w-3" /> Airport
            </p>
            <p className="mt-0.5 font-mono text-sm font-bold">{p.airportKm} KM</p>
          </div>
        </div>

        <div className="mt-5 flex items-end justify-between border-t border-border pt-4">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              Base Rate
            </p>
            <p className="font-display text-2xl font-extrabold tracking-tight">
              {inr(p.pricePerSqYd)}
              <span className="font-mono text-[10px] font-normal text-muted-foreground">
                {" "}
                / SQ YD
              </span>
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
              All-in Base
            </p>
            <p className="font-mono text-sm font-bold text-accent">{inr(total)}</p>
          </div>
        </div>

        <button
          onClick={() => onToggleCompare(p)}
          className={`mt-4 flex items-center gap-2 self-start font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
            inCompare ? "text-accent" : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`compare-toggle-${p.id}`}
        >
          {inCompare ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          {inCompare ? "In compare tray" : "Add to compare"}
        </button>

        <div className="mt-3 flex gap-2">
          <Button
            onClick={() => onLead(p, "interest")}
            className="h-10 flex-1 rounded-none font-mono text-[11px] uppercase tracking-[0.15em]"
            data-testid={`express-interest-${p.id}`}
          >
            Express Interest
            <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            onClick={() => onLead(p, "visit")}
            className="h-10 rounded-none font-mono text-[11px] uppercase tracking-[0.15em]"
            data-testid={`virtual-visit-${p.id}`}
          >
            <Video className="mr-1.5 h-3.5 w-3.5" />
            Site Visit
          </Button>
          <Button
            variant="outline"
            onClick={() => onCalc(p)}
            className="h-10 w-10 shrink-0 rounded-none p-0"
            data-testid={`calc-prefill-${p.id}`}
            title="Run the numbers"
          >
            <Calculator className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </motion.article>
  );
}
